import './featured-multiple-paper.scss'

import classNames from 'classnames'
import { graphql } from 'gatsby'
import isNil from 'lodash/isNil'
import React from 'react'

import Button from '@/components/button/button'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import { generateUrl } from '@/internal/utils/generate-url'
import { getModelMessages } from '@/utils/get-model-messages'

const FeaturedMultiplePaper: React.FC<
  Queries.SectionFeaturedPapersFragment
> = ({ featuredPapers, title }) => {
  if (isNil(featuredPapers) || featuredPapers.length < 2) {
    return <></>
  }
  const numberOfPairs = Math.ceil(featuredPapers.length / 2)
  const paperPairs = Array.from({ length: numberOfPairs }, (_, pairIndex) =>
    featuredPapers.slice(pairIndex * 2, pairIndex * 2 + 2)
  )

  return (
    <div className="featured-papers">
      <div className="container">
        <div className="row">
          <div className="col">
            <h2>{title}</h2>
          </div>
        </div>
        {paperPairs.map(
          (pair: Queries.SectionFeaturedPapersFragment['featuredPapers']) => {
            if (pair === null) return <></>
            const secondPaper = pair[1]

            return (
              <div className="row mb-4" key={pair[0]?.externalName}>
                {pair[0] && <FeaturedPaper paper={pair[0]} first />}
                {secondPaper && (
                  <FeaturedPaper paper={secondPaper} first={false} />
                )}
              </div>
            )
          }
        )}
      </div>
    </div>
  )
}

const FeaturedPaper: React.FC<{
  paper: {
    readonly __typename: 'DatoCmsPctPaper'
    readonly externalName: string | null
    readonly slug: string | null
    readonly excerpt: string | null
    readonly cover: {
      readonly gatsbyImageData: Record<string, unknown> | null
    } | null
    readonly topic: {
      readonly __typename: 'DatoCmsPctTopic'
      readonly originalId: string
      readonly id: string
      readonly externalName: string | null
      readonly slug: string | null
    } | null
    readonly authors:
      | readonly ({
          readonly person: {
            readonly firstName: string | null
            readonly secondName: string | null
          } | null
        } | null)[]
      | null
  }
  first: boolean
}> = ({ paper, first }) => {
  if (paper.slug === null) return <></>

  const modelMessages = getModelMessages(paper.__typename)
  const urlContent = generateUrl('DatoCmsPctPaper', paper.slug)
  return (
    <>
      <div
        className={classNames(
          'col-md-7 col-xl-3',
          'mb-4 mb-md-5',
          { 'order-2': first },
          { 'order-4': !first },
          { 'order-lg-1': first },
          { 'order-lg-3': !first }
        )}
      >
        {paper?.topic?.slug && (
          <div className="topic">
            <Button
              to={generateUrl('DatoCmsPctTopic', paper?.topic?.slug)}
              title={paper.topic.externalName}
              color="tertiary"
            />
          </div>
        )}
        <TitleLink
          to={urlContent}
          title={paper.externalName ?? ''}
          htmltag="h3"
        />
        <div className="description">{paper.excerpt}</div>
        <div className="authors">
          {paper.authors && paper.authors.length > 0 && (
            <>
              <h4>Authors /</h4>
              {paper.authors.map((item) => (
                <div
                  className="name"
                  key={`${item?.person?.firstName} ${item?.person?.secondName}`}
                >
                  {item?.person?.firstName} {item?.person?.secondName}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="links">
          <Button to={urlContent} title={modelMessages.button} />
        </div>
      </div>
      <div
        className={classNames(
          'col-md-5 col-xl-3 mb-md-5',
          { 'order-1': first },
          { 'order-3': !first },
          { 'order-md-2': first },
          { 'order-lg-4': !first }
        )}
      >
        <Link to={urlContent}>
          <Image
            image={paper?.cover?.gatsbyImageData}
            alt={paper?.externalName ?? ''}
          />
        </Link>
      </div>
    </>
  )
}

export default FeaturedMultiplePaper

export const query = graphql`
  fragment SectionFeaturedPapers on DatoCmsFeaturedMultiplePaper {
    __typename
    id
    navigationLabel
    title
    featuredPapers {
      __typename
      ... on DatoCmsPctPaper {
        externalName
        slug
        cover {
          ...imgFeaturedMultiplePaper
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            __typename
            firstName
            secondName
          }
        }
      }
    }
  }
`
